import {
  getPointerCoords,
  isLineIntersectingEraser,
} from '../utils/whiteboardUtils';
import { getShapeBoundingBox, isPointInShape } from '../components/shapes';

export const useWhiteboardPointerHandlers = ({
  svgRef, panRef,
  shapes, setShapes,
  selectedShape, setSelectedShape,
  selectedShapes, setSelectedShapes,
  selectionStartRef,
  isGroupDragging,
  dragged, setDragged,
  tool, setTool,
  penType, penColor, penThickness,
  isDrawingShape, setIsDrawingShape,
  drawingShape, setDrawingShape,
  shapeStartPoint, setShapeStartPoint,
  isDrawing,
  currentLine, setCurrentLine,
  eraserSize, isErasing, setIsErasing,
  eraserTrail, setEraserTrail,
  eraserShadow, setEraserShadow,
  fadedShapes, setFadedShapes,
  eraserPrevRef,
  isResizing, setIsResizing,
  resizeStart, setResizeStart,
  originalSize, setOriginalSize,
  startShapes, setStartShapes,
  resizing, setResizing,
  setEditingTextId,
  stickerPopup, setStickerPopup,
  createTextBlock,
  updatePan,
}) => {
  const handlePointerDown = e => {
    e.stopPropagation();
    if (e.button !== undefined && e.button !== 0) return;
    if (e.target.closest('.whiteboard-toolbar-btn')) return;
    if (e.target.closest('foreignObject') || e.target.closest('[contenteditable="true"]')) return;

    const { x, y } = getPointerCoords(e, panRef.current, svgRef);

    // Resize handle check for selected shape
    if (selectedShape) {
      const shape = shapes.find(s => s.id === selectedShape);
      if (shape) {
        const isTouch = e.pointerType === 'touch' || (e.touches && e.touches.length > 0);
        const handleSize = isTouch ? 24 / panRef.current.scale : 8;
        const isRightHandle = Math.abs(x - (shape.x + shape.width)) < handleSize && Math.abs(y - (shape.y + shape.height / 2)) < handleSize;
        const isBottomHandle = Math.abs(x - (shape.x + shape.width / 2)) < handleSize && Math.abs(y - (shape.y + shape.height)) < handleSize;
        const isCornerHandle = Math.abs(x - (shape.x + shape.width)) < handleSize && Math.abs(y - (shape.y + shape.height)) < handleSize;
        if (isRightHandle || isBottomHandle || isCornerHandle) {
          let corner = 'se';
          if (isCornerHandle) {
            const isLeft = x < (shape.x + shape.width / 2);
            const isTop = y < (shape.y + shape.height / 2);
            corner = isLeft && isTop ? 'nw' : !isLeft && isTop ? 'ne' : isLeft && !isTop ? 'sw' : 'se';
          }
          setIsResizing(true);
          setResizeStart({ x, y, corner });
          setOriginalSize({ width: shape.width, height: shape.height });
          return;
        }
      }
    }

    if (tool === 'pen' || tool === 'highlighter') {
      isDrawing.current = true;
      setCurrentLine({
        points: [[x, y]],
        color: penType === 'eraser' ? '#fff' : penColor,
        thickness: penThickness,
        alpha: tool === 'highlighter' ? 0.5 : 1,
        type: penType,
      });
    } else if (['shape', 'circle', 'triangle', 'arrow', 'line', 'message'].includes(tool)) {
      const { x: sx, y: sy } = getPointerCoords(e, panRef.current, svgRef);
      setIsDrawingShape(true);
      setShapeStartPoint({ x: sx, y: sy });
      const id = Date.now();
      let tempShape;
      if (tool === 'shape') tempShape = { id, type: 'rect', x: sx, y: sy, width: 0, height: 0, color: '#000', fill: 'none', strokeWidth: 1 };
      else if (tool === 'circle') tempShape = { id, type: 'circle', x: sx, y: sy, r: 0, color: '#000', fill: 'none', strokeWidth: 1 };
      else if (tool === 'triangle') tempShape = { id, type: 'triangle', x: sx, y: sy, width: 0, height: 0, color: '#000', fill: 'none', strokeWidth: 1 };
      else if (tool === 'arrow') tempShape = { id, type: 'arrow', x1: sx, y1: sy, x2: sx, y2: sy, color: '#000', thickness: 1 };
      else if (tool === 'line') tempShape = { id, type: 'line', x1: sx, y1: sy, x2: sx, y2: sy, color: '#000', thickness: 1 };
      else if (tool === 'message') tempShape = { id, type: 'message', x: sx, y: sy, width: 0, height: 0, color: '#000', fill: 'none', strokeWidth: 1 };
      setDrawingShape(tempShape);
      return;
    } else if (tool === 'select') {
      let shapeFound = false;
      let clickedShape = null;
      for (let i = shapes.length - 1; i >= 0; i--) {
        const s = shapes[i];
        if (s.type === 'pencil' && s.points && s.points.some(([px, py]) => Math.abs(px - x) < 8 && Math.abs(py - y) < 8)) {
          clickedShape = s; shapeFound = true; break;
        } else if (['image', 'video', 'audio', 'pdf', 'youtube', 'vimeo'].includes(s.type) &&
            x >= s.x && x <= s.x + s.width && y >= s.y && y <= s.y + s.height) {
          clickedShape = s; shapeFound = true; break;
        } else if (isPointInShape(x, y, s)) {
          clickedShape = s; shapeFound = true; break;
        }
      }

      if (shapeFound && clickedShape) {
        const box = getShapeBoundingBox(clickedShape);
        const handleSize = 8;
        const corners = [
          { corner: 'nw', x: (box.x - handleSize) * panRef.current.scale + panRef.current.x, y: (box.y - handleSize) * panRef.current.scale + panRef.current.y },
          { corner: 'ne', x: (box.x + box.width + handleSize) * panRef.current.scale + panRef.current.x, y: (box.y - handleSize) * panRef.current.scale + panRef.current.y },
          { corner: 'sw', x: (box.x - handleSize) * panRef.current.scale + panRef.current.x, y: (box.y + box.height + handleSize) * panRef.current.scale + panRef.current.y },
          { corner: 'se', x: (box.x + box.width + handleSize) * panRef.current.scale + panRef.current.x, y: (box.y + box.height + handleSize) * panRef.current.scale + panRef.current.y },
        ];
        let resizeHandleClicked = false;
        for (const corner of corners) {
          if (Math.sqrt((x - corner.x) ** 2 + (y - corner.y) ** 2) <= 50) {
            setSelectedShape(clickedShape.id);
            setIsResizing(true);
            setResizeStart({ x, y, corner: corner.corner });
            setOriginalSize({ width: clickedShape.width, height: clickedShape.height });
            setStartShapes([clickedShape]);
            resizeHandleClicked = true;
            break;
          }
        }
        if (!resizeHandleClicked) {
          if (e.shiftKey) {
            if (selectedShapes.includes(clickedShape.id)) {
              setSelectedShapes(prev => prev.filter(id => id !== clickedShape.id));
              setSelectedShape(null);
            } else {
              setSelectedShapes(prev => [...prev, clickedShape.id]);
              setSelectedShape(null);
            }
          } else {
            setSelectedShape(clickedShape.id);
            setSelectedShapes([]);
          }
          if (clickedShape.type === 'pencil' || clickedShape.type === 'line' || clickedShape.type === 'arrow') {
            setDragged({ id: clickedShape.id, type: clickedShape.type, lastX: x, lastY: y });
          } else {
            setDragged({ id: clickedShape.id, offsetX: x - clickedShape.x, offsetY: y - clickedShape.y });
          }
        }
      } else {
        if (selectedShapes.length > 0) {
          const group = shapes.filter(s => selectedShapes.includes(s.id));
          if (group.length > 0) {
            const xs = group.map(s => getShapeBoundingBox(s).x);
            const ys = group.map(s => getShapeBoundingBox(s).y);
            const ws = group.map(s => getShapeBoundingBox(s).x + getShapeBoundingBox(s).width);
            const hs = group.map(s => getShapeBoundingBox(s).y + getShapeBoundingBox(s).height);
            const isInGroupSelection = x >= Math.min(...xs) && x <= Math.max(...ws) && y >= Math.min(...ys) && y <= Math.max(...hs);
            if (isInGroupSelection) return;
          }
        }
        setSelectedShape(null);
        setSelectedShapes([]);
        document.body.style.cursor = 'default';
      }
    } else if (tool === 'eraser') {
      const { x: ex, y: ey } = getPointerCoords(e, panRef.current, svgRef);
      setIsErasing(true);
      setEraserTrail([{ x: ex, y: ey }]);
      setEraserShadow([{ x: ex, y: ey }]);
      setFadedShapes(new Set());
      const shapesToFade = shapes.filter(shape => {
        if (shape.type === 'pencil') return isLineIntersectingEraser(shape.points, ex, ey, eraserSize);
        return ex >= shape.x && ex <= shape.x + (shape.w || shape.width || 0) &&
               ey >= shape.y && ey <= shape.y + (shape.h || shape.height || 0);
      });
      setFadedShapes(new Set(shapesToFade.map(s => s.id)));
    } else if (tool === 'text') {
      const { x: tx, y: ty } = getPointerCoords(e, panRef.current, svgRef);
      const clickedTextBlock = shapes.find(s =>
        s.type === 'text' && tx >= s.x && tx <= s.x + (s.width || 200) && ty >= s.y && ty <= s.y + (s.height || 64)
      );
      if (clickedTextBlock) {
        setSelectedShape(clickedTextBlock.id);
        setEditingTextId(clickedTextBlock.id);
      } else {
        createTextBlock(tx, ty);
        setTool('select');
      }
      return; // skip catch-all deselection so selectedShape stays set
    } else if (tool === 'erase') {
      const { x: ex, y: ey } = getPointerCoords(e, panRef.current, svgRef);
      setShapes(prev => prev.filter(s => {
        if (s.type === 'pencil') return !s.points.some(([px, py]) => Math.hypot(px - ex, py - ey) <= 10);
        return true;
      }));
    } else if (tool === 'sticker' && stickerPopup) {
      if (e.target.closest('.sticker-popup')) return;
      setStickerPopup(false);
      return;
    }

    if (!e.target.closest('foreignObject') && !e.target.closest('[contenteditable="true"]')) {
      let shapeFound = false;
      const { x: cx, y: cy } = getPointerCoords(e, panRef.current, svgRef);
      for (let i = shapes.length - 1; i >= 0; i--) {
        const s = shapes[i];
        if (s.type === 'pencil' && s.points && s.points.some(([px, py]) => Math.abs(px - cx) < 8 && Math.abs(py - cy) < 8)) {
          shapeFound = true; break;
        } else if (['image', 'video', 'audio', 'pdf', 'youtube', 'vimeo'].includes(s.type) &&
            cx >= s.x && cx <= s.x + s.width && cy >= s.y && cy <= s.y + s.height) {
          shapeFound = true; break;
        } else if (isPointInShape(cx, cy, s)) {
          shapeFound = true; break;
        }
      }
      if (!shapeFound && tool !== 'select') {
        setSelectedShape(null);
        setSelectedShapes([]);
        selectionStartRef.current = null;
        document.body.style.cursor = 'default';
      }
    }
  };

  const handlePointerMove = e => {
    if (isDrawing.current && currentLine) {
      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
      setCurrentLine(line => line ? { ...line, points: [...line.points, [x, y]] } : null);
    }

    if (isDrawingShape && drawingShape && shapeStartPoint) {
      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
      setDrawingShape(prev => {
        if (!prev) return null;
        if (prev.type === 'rect' || prev.type === 'triangle' || prev.type === 'message') {
          return { ...prev, width: Math.abs(x - shapeStartPoint.x), height: Math.abs(y - shapeStartPoint.y) };
        } else if (prev.type === 'circle') {
          const r = Math.max(Math.abs(x - shapeStartPoint.x), Math.abs(y - shapeStartPoint.y)) / 2;
          return { ...prev, r, width: Math.abs(x - shapeStartPoint.x), height: Math.abs(y - shapeStartPoint.y) };
        } else if (prev.type === 'arrow' || prev.type === 'line') {
          return { ...prev, x2: x, y2: y };
        }
        return prev;
      });
    }

    if (tool === 'eraser' && isErasing) {
      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
      setEraserTrail(prev => [...prev, { x, y }]);
      setEraserShadow(prev => [...prev, { x, y }].slice(-8));
      const allTrailPoints = [...eraserTrail, { x, y }];
      const shapesToFade = shapes.filter(shape => {
        if (shape.type === 'pencil') {
          return allTrailPoints.some(tp => isLineIntersectingEraser(shape.points, tp.x, tp.y, eraserSize));
        }
        return x >= shape.x && x <= shape.x + (shape.w || shape.width || 0) &&
               y >= shape.y && y <= shape.y + (shape.h || shape.height || 0);
      });
      setFadedShapes(prev => new Set([...prev, ...shapesToFade.map(s => s.id)]));
    }

    if (resizing && resizing.shape && resizing.shape.type === 'sticker') {
      const cx = e.clientX ?? e.touches?.[0]?.clientX ?? e.changedTouches?.[0]?.clientX ?? 0;
      const cy = e.clientY ?? e.touches?.[0]?.clientY ?? e.changedTouches?.[0]?.clientY ?? 0;
      const dx = cx - resizing.startX;
      const dy = cy - resizing.startY;
      const minSize = 60;
      const corner = resizing.corner;
      let newW = resizing.startW, newH = resizing.startH;
      let newX = resizing.startX0, newY = resizing.startY0;
      if (Array.isArray(corner) && corner.length === 2) {
        if (corner[0] === 1) newW = Math.max(minSize, resizing.startW + dx);
        if (corner[0] === 0) { newW = Math.max(minSize, resizing.startW - dx); newX = resizing.startX0 + dx; }
        if (corner[1] === 1) newH = Math.max(minSize, resizing.startH + dy);
        if (corner[1] === 0) { newH = Math.max(minSize, resizing.startH - dy); newY = resizing.startY0 + dy; }
      }
      setShapes(prev => prev.map(sh => sh.id === resizing.shape.id ? { ...sh, w: newW, h: newH, x: newX, y: newY } : sh));
    }

    if (isResizing && selectedShape && startShapes) {
      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
      const dx = x - resizeStart.x, dy = y - resizeStart.y;
      const minSize = 50;
      const corner = resizeStart.corner || 'se';
      setShapes(prev => prev.map(s => {
        if (!s || s.id !== selectedShape) return s;
        const orig = startShapes.find(ss => ss.id === s.id);
        if (!orig) return s;

        let newWidth = Math.max(minSize, orig.width + dx);
        let newHeight = Math.max(minSize, orig.height + dy);
        let newX = orig.x, newY = orig.y;

        if (resizeStart.corner) {
          switch (corner) {
            case 'nw': newWidth = Math.max(minSize, orig.width - dx); newHeight = Math.max(minSize, orig.height - dy); newX = orig.x + dx; newY = orig.y + dy; break;
            case 'ne': newWidth = Math.max(minSize, orig.width + dx); newHeight = Math.max(minSize, orig.height - dy); newY = orig.y + dy; break;
            case 'sw': newWidth = Math.max(minSize, orig.width - dx); newHeight = Math.max(minSize, orig.height + dy); newX = orig.x + dx; break;
            case 'se': newWidth = Math.max(minSize, orig.width + dx); newHeight = Math.max(minSize, orig.height + dy); break;
            default: break;
          }
        }

        if (s.type === 'circle') {
          const startRadius = originalSize.r || orig.r || (orig.width / 2);
          let newRadiusX = startRadius, newRadiusY = startRadius;
          let newCenterX = orig.x, newCenterY = orig.y;
          switch (corner) {
            case 'nw': newRadiusX = Math.max(minSize / 2, startRadius - dx); newRadiusY = Math.max(minSize / 2, startRadius - dy); newCenterX = orig.x + (startRadius - newRadiusX); newCenterY = orig.y + (startRadius - newRadiusY); break;
            case 'ne': newRadiusX = Math.max(minSize / 2, startRadius + dx); newRadiusY = Math.max(minSize / 2, startRadius - dy); newCenterY = orig.y + (startRadius - newRadiusY); break;
            case 'sw': newRadiusX = Math.max(minSize / 2, startRadius - dx); newRadiusY = Math.max(minSize / 2, startRadius + dy); newCenterX = orig.x + (startRadius - newRadiusX); break;
            case 'se': newRadiusX = Math.max(minSize / 2, startRadius + dx); newRadiusY = Math.max(minSize / 2, startRadius + dy); break;
            default: break;
          }
          return { ...s, x: newCenterX, y: newCenterY, rx: newRadiusX, ry: newRadiusY };
        }

        if (s.type === 'pencil') {
          const orig = startShapes.find(ss => ss.id === s.id);
          if (!orig || !Array.isArray(orig.points) || orig.points.length === 0) return s;
          const pts = orig.points;
          const oMinX = Math.min(...pts.map(p => p[0]));
          const oMinY = Math.min(...pts.map(p => p[1]));
          const oMaxX = Math.max(...pts.map(p => p[0]));
          const oMaxY = Math.max(...pts.map(p => p[1]));
          const oW = oMaxX - oMinX || 1;
          const oH = oMaxY - oMinY || 1;
          let nMinX = oMinX, nMinY = oMinY, nW = Math.max(20, oW + dx), nH = Math.max(20, oH + dy);
          switch (corner) {
            case 'nw': nW = Math.max(20, oW - dx); nH = Math.max(20, oH - dy); nMinX = oMinX + dx; nMinY = oMinY + dy; break;
            case 'ne': nW = Math.max(20, oW + dx); nH = Math.max(20, oH - dy); nMinY = oMinY + dy; break;
            case 'sw': nW = Math.max(20, oW - dx); nH = Math.max(20, oH + dy); nMinX = oMinX + dx; break;
            case 'se': break;
            default: break;
          }
          const scaledPoints = pts.map(([px, py]) => [
            nMinX + ((px - oMinX) / oW) * nW,
            nMinY + ((py - oMinY) / oH) * nH,
          ]);
          return { ...s, points: scaledPoints };
        }

        if (s.type === 'arrow' || s.type === 'line') {
          const { x: lx, y: ly } = getPointerCoords(e, panRef.current, svgRef);
          const lorig = startShapes.find(ss => ss.id === s.id);
          if (!lorig) return s;
          if (resizeStart.end === 'start') return { ...s, x1: lx, y1: ly };
          if (resizeStart.end === 'end') return { ...s, x2: lx, y2: ly };
        }

        if (s.type === 'image' || s.type === 'pdf' || s.type === 'youtube') {
          const lorig = startShapes.find(ss => ss.id === s.id);
          if (!lorig) return s;
          let iw = Math.max(minSize, lorig.width + dx), ih = Math.max(minSize, lorig.height + dy);
          let ix = lorig.x, iy = lorig.y;
          switch (corner) {
            case 'nw': iw = Math.max(minSize, lorig.width - dx); ih = Math.max(minSize, lorig.height - dy); ix = lorig.x + dx; iy = lorig.y + dy; break;
            case 'ne': iw = Math.max(minSize, lorig.width + dx); ih = Math.max(minSize, lorig.height - dy); iy = lorig.y + dy; break;
            case 'sw': iw = Math.max(minSize, lorig.width - dx); ih = Math.max(minSize, lorig.height + dy); ix = lorig.x + dx; break;
            case 'se': iw = Math.max(minSize, lorig.width + dx); ih = Math.max(minSize, lorig.height + dy); break;
            default: break;
          }
          return { ...s, width: iw, height: ih, x: ix, y: iy };
        }

        return { ...s, x: newX, y: newY, width: newWidth, height: newHeight };
      }));
    }

    if (dragged && selectedShape) {
      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
      if (dragged.type === 'segment' || dragged.type === 'line' || dragged.type === 'arrow' || dragged.type === 'pencil') {
        const dx = x - (dragged.lastX ?? x), dy = y - (dragged.lastY ?? y);
        if (dx !== 0 || dy !== 0) {
          setShapes(prev => prev.map(s => {
            if (!s || s.id !== selectedShape) return s;
            if (s.type === 'pencil') {
              return { ...s, points: s.points.map(([px, py]) => [px + dx, py + dy]) };
            }
            if (s.type !== 'arrow' && s.type !== 'line') return s;
            const { x1, y1, x2, y2 } = s;
            if (!Number.isFinite(x1) || !Number.isFinite(y1) || !Number.isFinite(x2) || !Number.isFinite(y2)) return s;
            return { ...s, x1: x1 + dx, y1: y1 + dy, x2: x2 + dx, y2: y2 + dy };
          }));
          setDragged(prev => prev ? { ...prev, lastX: x, lastY: y } : prev);
        }
      } else {
        setShapes(prev => prev.map(s => {
          if (!s || s.id !== selectedShape) return s;
          return { ...s, x: x - dragged.offsetX, y: y - dragged.offsetY };
        }));
      }
    }

    if (dragged && dragged.id === 'board' && !selectedShape && selectedShapes.length === 0) {
      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
      updatePan(prev => ({ x: prev.x + (x - dragged.offsetX), y: prev.y + (y - dragged.offsetY), scale: prev.scale }));
      setDragged(prev => ({ ...prev, offsetX: x, offsetY: y }));
    }

    if (tool === 'select' && selectedShapes.length > 0 && !isGroupDragging) {
      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
      const group = shapes.filter(s => selectedShapes.includes(s.id));
      if (group.length > 0) {
        const xs = group.map(s => getShapeBoundingBox(s).x);
        const ys = group.map(s => getShapeBoundingBox(s).y);
        const ws = group.map(s => getShapeBoundingBox(s).x + getShapeBoundingBox(s).width);
        const hs = group.map(s => getShapeBoundingBox(s).y + getShapeBoundingBox(s).height);
        const isMouseInSelection = x >= Math.min(...xs) && x <= Math.max(...ws) && y >= Math.min(...ys) && y <= Math.max(...hs);
        document.body.style.cursor = isMouseInSelection ? 'grab' : 'default';
      }
    }
  };

  const handlePointerUp = () => {

    if (tool === 'select' && selectedShapes.length > 0) document.body.style.cursor = 'default';

    if (isDrawing.current && currentLine && currentLine.points.length > 1) {
      setShapes(prev => [...prev, { ...currentLine, id: Date.now(), type: 'pencil' }]);
    }

    if (isDrawingShape && drawingShape) {
      const { x1, y1, x2, y2 } = drawingShape;
      if (drawingShape.type === 'rect' && (drawingShape.width > 5 || drawingShape.height > 5)) setShapes(prev => [...prev, drawingShape]);
      else if (drawingShape.type === 'circle' && (drawingShape.r > 5 || drawingShape.width > 5)) {
        setShapes(prev => [...prev, { ...drawingShape, width: drawingShape.width || drawingShape.r * 2, height: drawingShape.height || drawingShape.r * 2 }]);
      } else if (drawingShape.type === 'triangle' && (drawingShape.width > 5 || drawingShape.height > 5)) setShapes(prev => [...prev, drawingShape]);
      else if (drawingShape.type === 'arrow' && (Math.abs(x2 - x1) > 5 || Math.abs(y2 - y1) > 5)) setShapes(prev => [...prev, drawingShape]);
      else if (drawingShape.type === 'line' && (Math.abs(x2 - x1) > 5 || Math.abs(y2 - y1) > 5)) setShapes(prev => [...prev, drawingShape]);
      else if (drawingShape.type === 'message' && (drawingShape.width > 5 || drawingShape.height > 5)) setShapes(prev => [...prev, drawingShape]);
    }

    setCurrentLine(null);
    isDrawing.current = false;
    setIsDrawingShape(false);
    setDrawingShape(null);
    setShapeStartPoint(null);
    setIsResizing(false);
    setResizeStart(null);
    setOriginalSize(null);
    setStartShapes(null);
    setResizing(null);
    setDragged(null);

    if (['shape', 'circle', 'triangle', 'arrow', 'line', 'message'].includes(tool)) setTool('select');

    if (tool === 'eraser') {
      if (fadedShapes.size > 0) setShapes(prev => prev.filter(shape => !fadedShapes.has(shape.id)));
      setIsErasing(false);
      setEraserTrail([]);
      setEraserShadow([]);
      setFadedShapes(new Set());
      eraserPrevRef.current = null;
    }
  };

  return { handlePointerDown, handlePointerMove, handlePointerUp };
};
